import type {
  WorkspaceElement, LEDProcessor, LEDPanel, PowerCabinet,
  DataPage, PowerPage, CableRoute, LEDConfig,
} from '@/types/synoptique'
import type {
  MireInfo, ProcessorDetail, PanelSummary, CabinetSummary,
  CablesData, ConnectionsData,
} from './report-types'

export function collectAllElements(
  dataPages: DataPage[],
  powerPages: PowerPage[],
): WorkspaceElement[] {
  const all: WorkspaceElement[] = []
  const seen = new Set<string>()
  for (const page of [...dataPages, ...powerPages]) {
    for (const el of page.workspaceElements) {
      if (!seen.has(el.id)) { seen.add(el.id); all.push(el) }
    }
  }
  return all
}

export function collectMires(dataPages: DataPage[]): MireInfo[] {
  const mires: MireInfo[] = []
  const seen = new Set<string>()
  for (const page of dataPages) {
    for (const el of page.workspaceElements) {
      if (el.type !== 'image') continue
      const data = el.data as Record<string, unknown>
      if (!data?.mireConfig) continue
      const config = data.mireConfig as LEDConfig
      const lw = config.ledWallMode
      const linked = (data.linkedPanel as LEDPanel) || null
      const pw = linked?.resolution?.width || lw.panelWidth
      const ph = linked?.resolution?.height || lw.panelHeight
      const rawName = (data.name as string) || config.name || `LED Wall ${mires.length + 1}`
      const shortName = rawName.split(' - ')[0].trim()
      const resW = pw * lw.columns
      const resH = ph * lw.rows
      // Deduplicate: same name AND same resolution = same wall
      const dedupeKey = `${shortName}|${resW}x${resH}`
      if (seen.has(dedupeKey)) continue
      seen.add(dedupeKey)
      mires.push({
        id: el.id, name: shortName,
        columns: lw.columns, rows: lw.rows,
        panelWidth: lw.panelWidth, panelHeight: lw.panelHeight,
        totalPanels: lw.columns * lw.rows,
        linkedPanel: linked,
        totalPixels: { width: resW, height: resH },
        pixelsPerPanel: { width: pw, height: ph },
      })
    }
  }
  return mires
}

export function collectProcessors(
  allElements: WorkspaceElement[],
  backupProcessors: Record<string, string>,
): ProcessorDetail[] {
  const results: ProcessorDetail[] = []
  for (const el of allElements) {
    if (el.type !== 'processor') continue
    const p = el.data as LEDProcessor
    const isBackup = Object.values(backupProcessors).includes(el.id)
    let backupOfLabel: string | null = null
    if (isBackup) {
      const mainId = Object.entries(backupProcessors).find(([, bId]) => bId === el.id)?.[0]
      if (mainId) {
        const mainEl = allElements.find(e => e.id === mainId)
        const mainP = mainEl?.data as LEDProcessor | undefined
        backupOfLabel = mainP ? (mainP.customName || `${mainP.brand} ${mainP.model}`) : mainId
      }
    }
    results.push({
      id: el.id, brand: p.brand, model: p.model, customName: p.customName,
      outputPorts: p.outputPorts, powerConsumption: p.powerConsumption,
      isBackup, backupOfLabel,
    })
  }
  return results
}

export function collectPanels(
  allElements: WorkspaceElement[],
  mires: MireInfo[],
): PanelSummary[] {
  const map = new Map<string, PanelSummary>()
  for (const el of allElements) {
    if (el.type !== 'panel') continue
    const p = el.data as LEDPanel
    const key = `${p.brand}-${p.name}-${p.pixelPitch}`
    const ex = map.get(key)
    if (ex) { ex.count++ } else {
      map.set(key, { brand: p.brand, name: p.name, pixelPitch: p.pixelPitch, resolution: p.resolution, dimensions: p.dimensions, powerConsumption: p.powerConsumption, count: 1 })
    }
  }
  for (const mire of mires) {
    if (!mire.linkedPanel) continue
    const p = mire.linkedPanel
    const key = `${p.brand}-${p.name}-${p.pixelPitch}`
    const ex = map.get(key)
    if (ex) { ex.count += mire.totalPanels } else {
      map.set(key, { brand: p.brand, name: p.name, pixelPitch: p.pixelPitch, resolution: p.resolution, dimensions: p.dimensions, powerConsumption: p.powerConsumption, count: mire.totalPanels })
    }
  }
  return Array.from(map.values())
}

export function collectCabinets(allElements: WorkspaceElement[]): CabinetSummary[] {
  const map = new Map<string, CabinetSummary>()
  for (const el of allElements) {
    if (el.type !== 'cabinet') continue
    const c = el.data as PowerCabinet
    const key = `${c.brand}-${c.name}`
    const ex = map.get(key)
    if (ex) { ex.count++ } else {
      map.set(key, { brand: c.brand, name: c.name, maxPower: c.maxPower, outlets: c.outlets, voltage: c.voltage, count: 1 })
    }
  }
  return Array.from(map.values())
}

export function collectCables(dataPages: DataPage[], powerPages: PowerPage[]): CablesData {
  const allCables: CableRoute[] = []
  for (const p of dataPages) allCables.push(...p.cableRoutes)
  for (const p of powerPages) allCables.push(...p.cableRoutes)
  return {
    dataCount: allCables.filter(c => c.type === 'data').length,
    powerCount: allCables.filter(c => c.type === 'power').length,
    total: allCables.length,
  }
}

export function collectConnections(
  dataPages: DataPage[],
  powerPages: PowerPage[],
  backupPorts: Record<string, Record<number, number>>,
): ConnectionsData {
  const usedDataPorts = new Set<string>()
  const usedPowerOutlets = new Set<string>()
  const firstDataSet = new Set<string>()
  const firstPowerSet = new Set<string>()

  for (const page of dataPages) {
    for (const c of page.panelConnections) {
      usedDataPorts.add(`${c.processorId}-P${c.port}`)
      if (c.order === 1) firstDataSet.add(`${c.processorId}-P${c.port}`)
    }
    for (const c of (page.mireConnections || [])) {
      usedDataPorts.add(`${c.processorId}-P${c.port}`)
      if (c.order === 1) firstDataSet.add(`${c.processorId}-P${c.port}`)
    }
  }
  for (const page of powerPages) {
    for (const c of page.powerConnections) {
      usedPowerOutlets.add(`${c.cabinetId}-O${c.outlet}`)
      if (c.order === 1) firstPowerSet.add(`${c.cabinetId}-O${c.outlet}`)
    }
    for (const c of (page.mirePowerConnections || [])) {
      usedPowerOutlets.add(`${c.cabinetId}-O${c.outlet}`)
      if (c.order === 1) firstPowerSet.add(`${c.cabinetId}-O${c.outlet}`)
    }
  }

  let redundancyPorts = 0
  for (const portMap of Object.values(backupPorts)) {
    redundancyPorts += Object.keys(portMap).length
  }

  // First Data = 1 câble par chaîne + 1 câble de redondance par chaîne
  // dont le port primaire a un backup (backup → dernier panneau).
  let firstData = firstDataSet.size
  for (const key of firstDataSet) {
    const m = key.match(/^(.*)-P(\d+)$/)
    if (m && backupPorts[m[1]]?.[Number(m[2])] !== undefined) firstData++
  }

  return {
    usedDataPorts: usedDataPorts.size,
    usedPowerOutlets: usedPowerOutlets.size,
    firstData,
    firstPower: firstPowerSet.size,
    redundancyPorts,
  }
}
